import { computed, toValue, type MaybeRefOrGetter, type Ref } from "vue";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { useApi, useToaster } from "@shopexperts/shared";
import type {
  AiStepRunStatus,
  AiSummaryKind,
  AiSummaryStep,
  ExpertAiStepRun,
  UpdateExpertAiContextRequest,
} from "@shopexperts/legacy-api";
import {
  AI_SUMMARY_KIND_LABELS,
  AI_SUMMARY_STEP_LABELS,
  aiSummariesErrorMessage,
  overviewHasActiveRuns,
} from "./aiSummaries";

const ACTIVE_POLL_INTERVAL_MS = 5_000;

function expertKey(expertId: number) {
  return ["admin", "expert", expertId, "ai-summaries"] as const;
}

export function useExpertAiSummaries(expertId: MaybeRefOrGetter<number | null | undefined>) {
  const api = useApi();
  const toaster = useToaster();
  const queryClient = useQueryClient();

  const id = computed(() => Number(toValue(expertId)));
  const enabled = computed(() => Number.isFinite(id.value) && id.value > 0);

  const overviewQuery = useQuery({
    queryKey: computed(() => [...expertKey(id.value), "overview"]),
    queryFn: () => api.admin.expertAiSummaries.getOverview({ params: { expertId: id.value } }),
    enabled,
    staleTime: 10_000,
    refetchInterval: (query) => (overviewHasActiveRuns(query.state.data) ? ACTIVE_POLL_INTERVAL_MS : false),
  });

  const contextQuery = useQuery({
    queryKey: computed(() => [...expertKey(id.value), "context"]),
    queryFn: () => api.admin.expertAiSummaries.getContext({ params: { expertId: id.value } }),
    enabled,
    staleTime: 60_000,
  });

  function invalidateAll() {
    queryClient.invalidateQueries({ queryKey: expertKey(id.value) });
  }

  const generate = useMutation({
    mutationFn: () => api.admin.expertAiSummaries.generate({ params: { expertId: id.value } }),
    onSuccess: (result) => {
      toaster.create({
        title: `Pipeline queued: ${result.runs.length} step${result.runs.length === 1 ? "" : "s"}`,
        type: "success",
      });
      invalidateAll();
    },
    onError: (error: Error) => {
      toaster.create({ title: `Couldn't start generation: ${aiSummariesErrorMessage(error)}`, type: "error" });
    },
  });

  const regenerateStep = useMutation({
    mutationFn: ({ step, cascade }: { step: AiSummaryStep; cascade: boolean }) =>
      api.admin.expertAiSummaries.regenerateStep({ params: { expertId: id.value, step }, body: { cascade } }),
    onSuccess: (result, { step, cascade }) => {
      const downstream = result.runs.length - 1;
      toaster.create({
        title: cascade && downstream > 0
          ? `${AI_SUMMARY_STEP_LABELS[step]} queued with ${downstream} downstream step${downstream === 1 ? "" : "s"}`
          : `${AI_SUMMARY_STEP_LABELS[step]} queued`,
        type: "success",
      });
      invalidateAll();
    },
    onError: (error: Error) => {
      toaster.create({ title: `Couldn't regenerate: ${aiSummariesErrorMessage(error)}`, type: "error" });
    },
  });

  const publish = useMutation({
    mutationFn: ({ kind, runId }: { kind: AiSummaryKind; runId: number }) =>
      api.admin.expertAiSummaries.publish({ params: { expertId: id.value, kind }, body: { run_id: runId } }),
    onSuccess: (result) => {
      toaster.create({ title: `${AI_SUMMARY_KIND_LABELS[result.kind]} revision ${result.revision} is now live`, type: "success" });
      invalidateAll();
    },
    onError: (error: Error) => {
      toaster.create({ title: `Couldn't publish revision: ${aiSummariesErrorMessage(error)}`, type: "error" });
    },
  });

  const updateContext = useMutation({
    mutationFn: (body: UpdateExpertAiContextRequest) =>
      api.admin.expertAiSummaries.updateContext({ params: { expertId: id.value }, body }),
    onSuccess: (result) => {
      queryClient.setQueryData([...expertKey(id.value), "context"], result);
      toaster.create({ title: "Expert AI context saved", type: "success" });
    },
    onError: (error: Error) => {
      toaster.create({ title: `Couldn't save context: ${aiSummariesErrorMessage(error)}`, type: "error" });
    },
  });

  return {
    expertId: id,
    overview: computed(() => overviewQuery.data.value ?? null),
    overviewQuery,
    context: computed(() => contextQuery.data.value ?? null),
    contextQuery,
    generate,
    regenerateStep,
    publish,
    updateContext,
    invalidateAll,
  };
}

export interface UseExpertAiStepRunsOptions {
  step?: MaybeRefOrGetter<AiSummaryStep | null | undefined>;
  status?: MaybeRefOrGetter<AiStepRunStatus | null | undefined>;
  enabled?: MaybeRefOrGetter<boolean>;
  limit?: number;
}

export function useExpertAiStepRuns(
  expertId: MaybeRefOrGetter<number | null | undefined>,
  options: UseExpertAiStepRunsOptions = {},
) {
  const api = useApi();
  const id = computed(() => Number(toValue(expertId)));
  const step = computed(() => toValue(options.step) ?? undefined);
  const status = computed(() => toValue(options.status) ?? undefined);
  const limit = options.limit ?? 20;
  const enabled = computed(() => (toValue(options.enabled) ?? true) && Number.isFinite(id.value) && id.value > 0);

  const query = useInfiniteQuery({
    queryKey: computed(() => [...expertKey(id.value), "runs", { step: step.value ?? null, status: status.value ?? null, limit }]),
    queryFn: async ({ pageParam }) =>
      api.admin.expertAiSummaries.listRuns({
        params: { expertId: id.value },
        query: {
          ...(step.value ? { step: step.value } : {}),
          ...(status.value ? { status: status.value } : {}),
          limit,
        },
        cursor: pageParam,
      }),
    getNextPageParam: (lastPage) => (lastPage.pagination.has_next ? lastPage.pagination.cursor ?? undefined : undefined),
    initialPageParam: undefined as string | undefined,
    enabled,
    staleTime: 10_000,
  });

  const runs = computed<ExpertAiStepRun[]>(() => query.data.value?.pages.flatMap((page) => page.data) ?? []);
  const total = computed(() => query.data.value?.pages[0]?.meta?.total ?? runs.value.length);

  return {
    runs,
    total,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    query,
  };
}

export function useExpertAiStepRun(
  expertId: MaybeRefOrGetter<number | null | undefined>,
  runId: Ref<number | null>,
) {
  const api = useApi();
  const id = computed(() => Number(toValue(expertId)));
  const enabled = computed(() => Number.isFinite(id.value) && id.value > 0 && typeof runId.value === "number" && runId.value > 0);

  const query = useQuery({
    queryKey: computed(() => [...expertKey(id.value), "run", runId.value]),
    queryFn: () => api.admin.expertAiSummaries.getRun({ params: { expertId: id.value, runId: runId.value as number } }),
    enabled,
    staleTime: 5 * 60_000,
  });

  return {
    run: computed(() => query.data.value ?? null),
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}
